import UsersStore from './users'
import SprintsStore from './sprints'
import UiLstSprintsStore from './ui-lst-sprints'
import WorkProgressStore from './work-progress'
import SubscriptionsStore from './subscriptions'
import AppTypeStore from '../cls/stores/AppTypeStore'
import UiLstRequestsStore from './ui-lst-requests'
import RequestsStore from './requests'
import TourStore from './tour'
import UiLstMonthlyReportsStore from './ui-lst-monthly-reports'
import MonthlyReportsStore from './monthly-reports'
import { getCookie } from '../config/utils'
import SecuredStore from './secured'
import NotificationsStore from './notifications'
import UiLstNotificationsStore from './ui-lst-notification'
import UiLstDocumentationStore from './ui-lst-documentations'
import DocumentationStore from './documentation'
import UiLstRecruitmentStore from './ui-lst-recruitment'
import RecruitmentStore from './recruitment'
import UiLstUsersStore from './ui-lst-users'
import BudgetStore from './budget'
import UiLstBudgetStore from './ui-lst-budget'
import MeetingStore from './meetings'
import UiLstMeetingsStore from './ui-lst-meetings'
import UiLstWorkflowsStore from './ui-lst-workflows'
import WorkflowsStore from './workflows'
import { translate } from '../cls/TranslationContext'
import { makeObservable, observable } from 'mobx'

class RootStore {
	modifiedFrequency = 10 // in seconds :: api request frequency for modified data -> see <Modified /> component
	categories = {}
	priorityOptions

	/**
	 * Specific id's will be added to the array
	 * If the id is in the array, the interval will start and the page will load data on interval set for that page
	 * If the id is not in the array, the interval will stop and the page will not load data on interval set for that page
	 * @type {Array}
	 * @memberof RootStore
	 */

	constructor() {
		this.tourStore = new TourStore()
		this.appType = new AppTypeStore(this)

		this.users = new UsersStore(this)
		this.uiLstUsers = new UiLstUsersStore(this)

		this.subscriptions = new SubscriptionsStore(this)

		this.uiLstWorkflows = new UiLstWorkflowsStore(this)
		this.workflows = new WorkflowsStore(this)

		this.meetings = new MeetingStore(this)
		this.uiLstMeetings = new UiLstMeetingsStore(this)

		this.sprints = new SprintsStore(this)
		this.uiLstSprints = new UiLstSprintsStore(this)

		this.monthlyReports = new MonthlyReportsStore(this)
		this.uiLstMonthlyReports = new UiLstMonthlyReportsStore(this)

		this.requests = new RequestsStore(this)
		this.uiLstRequests = new UiLstRequestsStore(this)

		this.notifications = new NotificationsStore(this)
		this.uiLstNotifications = new UiLstNotificationsStore(this)

		this.recruitment = new RecruitmentStore(this)
		this.uiLstRecruitment = new UiLstRecruitmentStore(this)

		this.documentation = new DocumentationStore(this)
		this.uiLstDocumentation = new UiLstDocumentationStore(this)

		this.budget = new BudgetStore(this)
		this.uiLstBudget = new UiLstBudgetStore(this)

		this.workProgress = new WorkProgressStore(this)
		this.secured = new SecuredStore(this)

		// TODO: Add this in utils not in constructor
		this.priorityOptions = [
			{
				key: 0,
				label: translate('single.low'),
				value: 0
			},
			{
				key: 1,
				label: translate('single.medium'),
				value: 1
			},
			{
				key: 2,
				label: translate('single.high'),
				value: 2
			},
			{
				key: 3,
				label: translate('single.urgent'),
				value: 3
			}
		]

		makeObservable(this, {
			categories: observable,
			modifiedFrequency: observable
		})
	}

	getCategoryIdByName = (categoryName) => {
		if (!categoryName || typeof categoryName !== 'string') return null
		for (const [key, category] of Object.entries(this.categories)) {
			if (
				category &&
				typeof category.en === 'string' &&
				category.en.trim().toLowerCase() ===
					categoryName.trim().toLowerCase()
			) {
				return key
			}
		}
		return null
	}

	shouldShowCookiePopover = () => {
		const loggedInUserId = this.users.loggedInUserId

		// Check if there is a logged-in user
		if (loggedInUserId) {
			const user = this.users.requireUserById(loggedInUserId)

			// Check if the user has a cookie consent stored in their preferences
			if (user && user.cookieConsent !== undefined) {
				return false
			}
		}

		// If the user is not logged in or has no cookie consent set, check the cookie
		const cookieConsent = getCookie('cookieConsent')
		if (cookieConsent !== null) {
			return false
		}

		// If no cookie consent is set, show the popover
		return true
	}

	getPlanById = (planId) => {
		const plans = this.subscriptions.plans
		const plan = plans.find((p) => p.id === planId)
		if (!plan) {
			return {
				price_id: planId,
				name: 'Unknown Plan'
			}
		}
		return plan
	}

	get userAllowedCategories() {
		const loggedInUserId = this.users.loggedInUserId
		const user = this.users.requireUserById(loggedInUserId)

		const activeCategoriesBasedOnSubscriptions =
			user.activeCategoriesBasedOnSubscriptions
		const hasActiveBacklineSubscription = user.hasActiveBacklineSubscription

		// If the user has an active backline subscription, return all categories
		if (hasActiveBacklineSubscription) {
			return this.categories
		}

		const userCategories = user.allowed?.categories || {}
		const userAllowedCategoryIds = Object.keys(userCategories) || []

		let allowedCategories = {}

		// Filter categories based on user allowed category IDs and demo status
		userAllowedCategoryIds.forEach((categoryId) => {
			const category = this.categories[categoryId]
			const isDemo = userCategories[categoryId]?.demo

			if (category && (isDemo === true || isDemo === 'true')) {
				allowedCategories[categoryId] = category
			}
		})

		// Add categories based on active subscriptions, avoiding duplicates
		if (Array.isArray(activeCategoriesBasedOnSubscriptions)) {
			activeCategoriesBasedOnSubscriptions.forEach((categoryName) => {
				const categoryId = this.getCategoryIdByName(categoryName)
				if (
					categoryId &&
					!allowedCategories.hasOwnProperty(categoryId)
				) {
					allowedCategories[categoryId] = this.categories[categoryId]
				}
			})
		}

		// Treat activeCategoriesBasedOnPurchases

		const activeCategoriesBasedOnPurchases =
			user.activeCategoriesBasedOnPurchases || []
		activeCategoriesBasedOnPurchases.forEach((categoryName) => {
			const categoryId = this.getCategoryIdByName(categoryName)
			if (categoryId && !allowedCategories.hasOwnProperty(categoryId)) {
				allowedCategories[categoryId] = this.categories[categoryId]
			}
		})

		return allowedCategories
	}

	setCategories = (categories) => {
		this.categories = categories
	}
}

const rootStore = new RootStore()
export default rootStore
